<template>
  <fragment>
    <AppBody
      title="Rede de Afiliados"
      :subtitle="$t('sales.affiliates_program')"
      :actions="actions"
    >
      <CardModule untitle>
        <ExpansionPanelFilters>
          <v-form
            @submit.prevent="filterAffiliatesPrograms"
            class="search-forms"
          >
            <v-row>
              <v-col cols="12" md="3" sm="4">
                <BaseTextField
                  :label="$t('sales.affiliates_program_name')"
                  :placeholder="$t('sales.affiliates_program_name_placeholder')"
                  v-model="filter.name"
                  type="text"
                >
                </BaseTextField>
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <BaseSelect
                  label="Status"
                  :placeholder="$t('sales.affiliates_program_status_placeholder')"
                  :items="status"
                  item-text="text"
                  item-value="value"
                  v-model="filter.status"
                  clearable
                ></BaseSelect>
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <BaseSelect
                  :label="$t('sales.affiliates_program_date_interval')"
                  :placeholder="$t('sales.affiliates_program_date_interval_placeholder')"
                  :items="intervalDates"
                  item-text="text"
                  item-value="value"
                  v-model="filter.dateInterval"
                />
              </v-col>
              <v-col cols="12" md="3" sm="4" v-if="isNotCustomRangeDates">
                <v-menu
                  ref="menu"
                  v-model="menu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <BaseTextField
                      :label="$t('sales.affiliates_program_dates')"
                      :placeholder="placeholderData"
                      v-model="dateRangeText"
                      :append-icon="$config.icons.calendar"
                      :disabled="disabledDatePicker"
                      v-bind="attrs"
                      v-on="on"
                      readonly
                      clearable
                      @click:clear="filter.rangeDates = []"
                    >
                    </BaseTextField>
                  </template>
                  <v-date-picker
                    v-model="computeRange"
                    @input="menu = false"
                    :active-picker.sync="activePicker"
                    @change="computeRange"
                    locale="pt"
                    :header-color="$config.dados.corinputs"
                    @click:month="monthEvent"
                    :color="$config.dados.corinputs"
                    range
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="3" sm="4" v-else>
                <v-menu
                  ref="menu"
                  v-model="menu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <BaseTextField
                      :label="$t('sales.affiliates_program_dates')"
                      :placeholder="placeholderData"
                      v-model="dateRangeText"
                      :append-icon="$config.icons.calendar"
                      v-bind="attrs"
                      v-on="on"
                      readonly
                      clearable
                      @click:clear="filter.rangeDates = []"
                    ></BaseTextField>
                  </template>
                  <v-date-picker
                    v-model="filter.rangeDates"
                    @change="filter.rangeDates"
                    locale="pt"
                    @input="
                      filter.rangeDates.length == 2
                        ? (menu = false)
                        : (menu = true)
                    "
                    :header-color="$config.dados.corinputs"
                    landscape
                    :color="$config.dados.corinputs"
                    range
                  >
                  </v-date-picker>
                </v-menu>
              </v-col>
              <v-col class="d-flex justify-end">
                <BaseButton
                  variant="filter"
                  type="submit"
                  :loading="affiliatesProgramsLoading"
                >
                  {{ $t('common.filter') }}
                </BaseButton>
              </v-col>
            </v-row>
          </v-form>
        </ExpansionPanelFilters>

        <v-data-table
          class="table-body"
          :headers="headers"
          :items="allAffiliatesPrograms"
          :loading="affiliatesProgramsLoading"
          :no-data-text="$t('sales.no_affiliates_program')"
          :loading-text="$t('sales.loading_affiliates_program')"
          item-key="idprograma"
          :items-per-page="10"
          :footer-props="{
            itemsPerPageOptions: [10, 20, 50],
            itemsPerPageText: $t('sales.affiliates_program_by_page'),
          }"
        >
          <template v-slot:item.actions="{ item }">
            <div
              style="display: flex; justify-content: space-evenly; gap: 16px"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :color="$config.dados.corinputs"
                    height="10"
                    width="10"
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="duplicateProgram(item)"
                  >
                    <v-icon>mdi-content-duplicate</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('sales.affiliates_duplicate_program') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :color="$config.dados.corinputs"
                    height="10"
                    width="10"
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="editProgramTickets(item)"
                  >
                    <v-icon>mdi-tag-edit-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('sales.affiliates_edit_ticket_label') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :color="$config.dados.corinputs"
                    height="10"
                    width="10"
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="getAffiliatesToResendEmail(item)"
                  >
                    <v-icon>mdi-email-fast-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('sales.affiliates_resend_invite_label') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :color="$config.dados.corinputs"
                    height="10"
                    width="10"
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="getAffiliatesToAdd(item)"
                  >
                    <v-icon>mdi-account-multiple-plus</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('sales.affiliates_program_manage_label') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :color="$config.dados.corinputs"
                    height="10"
                    width="10"
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="openChangeStatusModal(item)"
                  >
                    <v-icon>mdi-account-arrow-down</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('sales.affiliates_program_change_status_label') }}</span>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              style="align-items: center; justify-content: center"
              :color="item.status == 'ATIVO' ? 'success' : 'error'"
              dark
              small
            >
              {{ item.status }}
            </v-chip>
          </template>
          <template v-slot:item.nome="{ item }">
            {{ item.nome || "-" }}
          </template>
          <template v-slot:item.data_cadastro="{ item }">
            {{ formatDateTime(item.data_cadastro) || "-" }}
          </template>
          <template v-slot:item.valor="{ item }">
            {{ formatCurrency(item.valor) || "-" }}
          </template>
          <template v-slot:item.comissao_afiliado="{ item }">
            {{ formatCurrency(item.comissao_afiliado) || "-" }}
          </template>
          <template v-slot:item.desconto_afiliado="{ item }">
            {{ formatCurrency(item.desconto_afiliado) || "-" }}
          </template>
          <template v-slot:item.taxa_comissao="{ item }">
            {{
              item.tipo_comissao === "VALOR"
                ? formatCurrency(item.taxa_comissao)
                : item.taxa_comissao + "%"
            }}
          </template>
          <template v-slot:item.valor_desconto="{ item }">
            {{ formatCurrency(item.valor_desconto) || "-" }}
          </template>
          <template v-slot:item.perc_desconto="{ item }">
            {{ item.perc_desconto + "%" || "-" }}
          </template>
        </v-data-table>
      </CardModule>
    </AppBody>
    <ConfirmDialog
      :dialogText="$t('sales.affiliates_program_change_status')"
      @callback="changeStatusAffiliatesPrograms"
    />
    <DialogInsertAffiliate />
    <DialogResendEmailAffiliate />
    <DialogEditTicket v-if="dialogEditTicket" />
  </fragment>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import AppBody from "@/layouts/components/AppBody.vue";
import CardModule from "@/components/cards/CardModule.vue";
import BaseSelect from "@/components/forms/BaseSelect.vue";
import BaseTextField from "@/components/forms/BaseTextField.vue";
import BaseButton from "@/components/forms/BaseButton.vue";
import ConfirmDialog from "@/components/global/ConfirmDialog.vue";
import dayjs from "dayjs";
import DialogInsertAffiliate from "@/components/vendas/AffiliatesProgram/DialogInsertAffilite.vue";
import DialogResendEmailAffiliate from "@/components/vendas/AffiliatesProgram/DialogResendEmailAffiliate.vue";
import DialogEditTicket from "@/components/vendas/AffiliatesProgram/DialogEditTicket.vue";
import ExpansionPanelFilters from "@/components/forms/ExpansionPanelFilters.vue";

export default {
  name: "AffiliatesProgram",
  components: {
    AppBody,
    CardModule,
    BaseButton,
    BaseSelect,
    BaseTextField,
    ConfirmDialog,
    DialogInsertAffiliate,
    DialogResendEmailAffiliate,
    DialogEditTicket,
    ExpansionPanelFilters,
  },
  metaInfo: {
    title: "Rede de Afiliados | ElevenTickets",
    meta: [
      {
        content: "Rede de Afiliados | ElevenTickets",
      },
    ],
  },
  data() {
    return {
      dayjs: dayjs,
      isDeleting: false,
      itemDeleting: null,
      placeholderData: "dd/mm/aa ~ dd/mm/aa",
      menu: false,
      activePicker: null,
      filter: {
        name: "",
        status: "ATIVO",
        dateInterval: "",
        rangeDates: [],
      },
      status: [
        { text: "Ativo", value: "ATIVO" },
        { text: "Inativo", value: "INATIVO" },
      ]
    };
  },
  computed: {
    ...mapGetters([
      "allAffiliatesPrograms",
      "affiliatesProgramsLoading",
      "dialogEditTicket",
      "dialogResendEmail",
      "intervalDates",
    ]),
    computeRange: {
      get() {
        return this.filter.rangeDates;
      },
      set([firstDay]) {
        switch (this.filter.dateInterval) {
          case "date": {
            this.filter.rangeDates = [firstDay, firstDay];
            break;
          }
          case "week": {
            const currentDayWeek = this.dayjs(firstDay)
              .day(0)
              .format("YYYY-MM-DD");
            const week = this.dayjs(firstDay).day(6).format("YYYY-MM-DD");
            this.filter.rangeDates = [currentDayWeek, week];
            break;
          }
          case "month": {
            const currentDayMonth = this.dayjs(firstDay)
              .startOf("month")
              .format("YYYY-MM-DD");
            const month = this.dayjs(firstDay)
              .endOf("month")
              .format("YYYY-MM-DD");
            this.filter.rangeDates = [currentDayMonth, month];
            break;
          }
          case "custom": {
            const currentDayCustom = new Date(
              Date.now() - new Date().getTimezoneOffset() * 60000
            )
              .toISOString()
              .substr(0, 10);
            const custom = "";
            this.filter.rangeDates = [currentDayCustom, custom];
            break;
          }
          default: {
            const defaultDay = "";
            this.filter.rangeDates = [firstDay, defaultDay];
            break;
          }
        }
      },
    },
    headers() {
      return [
        {
          text: this.$t('sales.affiliates_programname'),
          align: "center",
          sortable: true,
          value: "nome",
          width: "200px",
        },
        {
          text: this.$t('sales.affiliates_program_creation'),
          align: "center",
          sortable: true,
          value: "data_cadastro",
          width: "180px",
        },
        {
          text: "Status",
          align: "center",
          sortable: false,
          value: "status",
          width: "120px",
        },
        {
          text: this.$t('sales.affiliates_program_discount_value'),
          align: "center",
          sortable: false,
          value: "valor_desconto",
          width: "120px",
        },
        {
          text: this.$t('sales.affiliates_program_discount_percent'),
          align: "center",
          sortable: false,
          value: "perc_desconto",
          width: "120px",
        },
        {
          text: this.$t('sales.affiliates_program_commission'),
          align: "center",
          sortable: false,
          value: "taxa_comissao",
          width: "120px",
        },
        {
          text: this.$t('sales.affiliates_program_commission_type'),
          align: "center",
          sortable: false,
          value: "tipo_comissao",
          width: "120px",
        },
        {
          text: this.$t('sales.affiliates_program_limit'),
          align: "center",
          sortable: false,
          value: "limite",
          width: "120px",
        },
        {
          text: this.$t('sales.affiliates_program_invites_sent'),
          align: "center",
          sortable: true,
          value: "convites_enviados",
          width: "160px",
        },
        {
          text: this.$t('sales.affiliates_program_invites_accepted'),
          align: "center",
          sortable: true,
          value: "convites_aceitos",
          width: "150px",
        },
        {
          text: this.$t('sales.affiliates_program_total_affiliates'),
          align: "center",
          sortable: true,
          value: "total_afiliados",
          width: "150px",
        },
        {
          text: this.$t('sales.affiliates_program_items_sold'),
          align: "center",
          sortable: false,
          value: "vendas",
          width: "120px",
        },
        {
          text: this.$t('sales.affiliates_program_total_sold'),
          align: "center",
          sortable: false,
          value: "valor",
          width: "120px",
        },
        {
          text: this.$t('sales.affiliates_program_affiliate_commission'),
          align: "center",
          sortable: false,
          value: "comissao_afiliado",
          width: "120px",
        },
        {
          text: this.$t('sales.affiliates_program_affiliate_discount'),
          align: "center",
          sortable: false,
          value: "desconto_afiliado",
          width: "120px",
        },
        {
          text: this.$t('sales.affiliates_program_actions'),
          align: "center",
          sortable: false,
          value: "actions",
          width: "120px",
        },
      ]
    },
    actions() {
      return [
        {
          name: this.$t('sales.affiliates_program_new'),
          action: () => {
            this.handleAction();
          },
        },
      ]
    },
    dateRangeText() {
      let arrayDates = [];
      this.filter.rangeDates.forEach(item => {
        return arrayDates.push(this.$config.convertDataBR(item));
      });

      return arrayDates.join(" ~ ");
    },
    isNotCustomRangeDates() {
      return this.filter.dateInterval !== "custom";
    },
    disabledDatePicker() {
      return this.filter.dateInterval != null ? false : true;
    },
  },
  watch: {
    "filter.dateInterval"(e) {
      switch (e) {
        case "date": {
          const currentDay = new Date(
            Date.now() - new Date().getTimezoneOffset() * 60000
          )
            .toISOString()
            .substr(0, 10);
          this.filter.rangeDates = [currentDay, currentDay];
          break;
        }
        case "week": {
          const currentDayWeek = this.dayjs().day(0).format("YYYY-MM-DD");
          const week = this.dayjs().day(6).format("YYYY-MM-DD");
          this.filter.rangeDates = [currentDayWeek, week];
          break;
        }
        case "month": {
          const currentDayMonth = this.dayjs()
            .startOf("month")
            .format("YYYY-MM-DD");
          const month = this.dayjs().endOf("month").format("YYYY-MM-DD");
          this.filter.rangeDates = [currentDayMonth, month];
          break;
        }
        case "custom": {
          const currentDayCustom = new Date(
            Date.now() - new Date().getTimezoneOffset() * 60000
          )
            .toISOString()
            .substr(0, 10);
          const custom = new Date(
            new Date(currentDayCustom).setDate(
              new Date(currentDayCustom).getDate() + 1
            )
          )
            .toISOString()
            .slice(0, 10);
          this.filter.rangeDates = [currentDayCustom, custom];
          break;
        }
        default: {
          const defaultDay = new Date(
            Date.now() - new Date().getTimezoneOffset() * 60000
          )
            .toISOString()
            .substr(0, 10);
          const defaultDayCustom = new Date(
            new Date(defaultDay).setDate(new Date(defaultDay).getDate() + 1)
          )
            .toISOString()
            .slice(0, 10);
          this.filter.rangeDates = [defaultDay, defaultDayCustom];
          break;
        }
      }
    },
    menu(val) {
      val &&
        setTimeout(() => {
          if (val && this.filter.dateInterval == "date") {
            this.activePicker = "DATE";
          } else if (val && this.filter.dateInterval == "week") {
            this.activePicker = "DATE";
          } else if (val && this.filter.dateInterval == "month") {
            this.activePicker = "MONTH";
          } else if (val && this.filter.dateInterval == "custom") {
            this.activePicker = "DATE";
          } else {
            this.activePicker = "DATE";
          }
        });
    },
  },
  methods: {
    ...mapActions([
      "fetchAffiliatesPrograms",
      "changeAffiliateProgramStatus",
      "changeDialogInsertAffiliate",
      "getInfoProgram",
      "cleanStates",
      "changeDialogResendEmail",
    ]),
    ...mapMutations(["setStepProgram", "toggleDialogEditTicket"]),
    handleAction() {
      this.cleanStates();
      this.$router.push({
        name: "afilliatesProgramSettings",
        params: { type: "cadastrar" },
      });
    },
    filterAffiliatesPrograms() {
      let filter = {};
      if (this.filter.rangeDates.length > 0) {
        Object.assign(filter, {
          limite: 1000,
          pagina: 1,
          name: this.filter.name,
          status: this.filter.status,
          data_inicial: `${this.filter.rangeDates[0]} 00:00`,
          data_final: `${this.filter.rangeDates[1]} 23:59`,
        });
      } else {
        Object.assign(filter, {
          limite: 1000,
          pagina: 1,
          name: this.filter.name,
          status: this.filter.status,
        });
      }
      this.fetchAffiliatesPrograms(filter);
    },
    formatCurrency(value) {
      if (!value) return "-";
      const formattedValue = parseFloat(value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      return formattedValue;
    },
    openChangeStatusModal(item) {
      this.$config.confirmDialog = true;
      this.itemDeleting = item;
    },
    changeStatusAffiliatesPrograms() {
      let filter = {
        id: this.itemDeleting.idprograma,
        status: this.itemDeleting.status === "ATIVO" ? "INATIVO" : "ATIVO",
      };
      this.changeAffiliateProgramStatus(filter).then(() => {
        this.$config.confirmDialog = false;
      });
    },
    monthEvent(month) {
      const currentDayMonth = this.dayjs(month)
        .startOf("month")
        .format("YYYY-MM-DD");
      const monthEnd = this.dayjs(month).endOf("month").format("YYYY-MM-DD");
      this.filter.rangeDates = [currentDayMonth, monthEnd];
    },
    formatDateTime(inputDateTime) {
      const dateObject = new Date(inputDateTime);

      const formattedDate = `${dateObject.getDate()}/${
        dateObject.getMonth() + 1
      }/${dateObject.getFullYear()}`;
      const formattedTime = `${dateObject.getHours()}:${String(
        dateObject.getMinutes()
      ).padStart(2, "0")}`;

      return `${formattedDate} - ${formattedTime}`;
    },
    getAffiliatesToAdd(item) {
      this.changeDialogInsertAffiliate({ idprograma: item.idprograma });
    },
    getAffiliatesToResendEmail(item) {
      this.changeDialogResendEmail({ idprograma: item.idprograma });
    },
    duplicateProgram(item) {
      this.setStepProgram(1);
      this.getInfoProgram({ id: item.idprograma });
      this.$router.push({
        name: "afilliatesProgramSettings",
        params: { type: "duplicar" },
      });
    },
    editProgramTickets(item) {
      this.toggleDialogEditTicket();
      this.getInfoProgram({ id: item.idprograma });
    },
  },
  mounted() {
    this.filterAffiliatesPrograms();
  },
};
</script>

<style>
.v-dialog {
  overflow-y: unset !important;
  border-radius: 50px !important;
}
.modal-header {
  padding: 15px;
}
</style>
